import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Eye, Brain, Key, BarChart3, Layers } from 'lucide-react';

const features = [
  {
    icon: ShieldCheck,
    title: "Advanced Encryption & Clinical Data Protection",
    description: "Military-grade 256-bit encryption specifically designed for healthcare AI training data and PHI with HIPAA compliance.",
    highlight: "AI-optimized encryption"
  },
  {
    icon: Eye,
    title: "Real-Time Threat Monitoring",
    description: "AI-powered detection system for adversarial attacks on clinical models with 99.8% accuracy in real-time threat identification.",
    highlight: "99.8% detection accuracy"
  },
  {
    icon: Brain,
    title: "Proactive Risk Prevention",
    description: "Predictive security using behavioral analysis of healthcare AI systems, preventing attacks before they occur in clinical environments.",
    highlight: "Predictive security"
  },
  {
    icon: Key,
    title: "Granular Access Control",
    description: "Time-based and role-based access restrictions for medical models. Admins, clinicians, and developers get appropriate access levels.",
    highlight: "OPA Integration"
  },
  {
    icon: BarChart3,
    title: "Logging & Productivity Insights",
    description: "Comprehensive audit trails with CRM integration. Compliance-ready reporting for FDA and HIPAA audits.",
    highlight: "Audit-ready reporting"
  },
  {
    icon: Layers,
    title: "End-to-End AI Lifecycle Protection",
    description: "From data ingestion to model training to deployment and monitoring in clinical settings. Seamless EMR integration.",
    highlight: "Complete coverage"
  }
];

export default function SolutionSection() {
  return (
    <section id="solutions" className="relative py-24 lg:py-32 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-0 w-[400px] h-[400px] rounded-full bg-[#2dd4bf]/10 blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] rounded-full bg-blue-500/10 blur-3xl" />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <p className="text-[#1e3a5f] text-sm font-medium tracking-wider uppercase mb-4">Our Solution</p>
          <h2 className="text-4xl md:text-6xl font-light text-slate-900 mb-6 leading-tight">
            Comprehensive Healthcare<br />
            <span className="bg-gradient-to-r from-[#2dd4bf] to-[#0d9488] bg-clip-text text-transparent font-medium">AI Security</span>
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            Complete healthcare AI lifecycle protection with healthcare-specific customization for clinical, genomics, and biotech applications.
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative p-8 rounded-2xl border border-slate-200 hover:border-[#2dd4bf]/50 hover:shadow-2xl hover:shadow-[#2dd4bf]/10 transition-all duration-500 bg-white"
            >
              <div className="w-14 h-14 rounded-2xl bg-slate-100 group-hover:bg-[#1e3a5f] flex items-center justify-center mb-6 transition-colors duration-300">
                <feature.icon className="w-7 h-7 text-[#1e3a5f] group-hover:text-white transition-colors duration-300" />
              </div>
              
              <span className="inline-block px-3 py-1 rounded-full bg-[#2dd4bf]/10 text-[#0d9488] text-xs font-medium mb-4">
                {feature.highlight}
              </span>
              
              <h3 className="text-xl font-medium text-slate-900 mb-3">{feature.title}</h3>
              <p className="text-slate-600 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}